import { useMemo, useCallback } from 'react';
import { FileUpload } from 'src/components/general/MultiFileLoader/MultiFileLoader';

export type PersonIdDataProps = {
  uploadCedulaAction: (fileName: string, base64Image: string) => unknown;
  cedulaFileName: string;
  cedulaFileBase64: string;
  cedulaFileStatus: string;
  uploadCedulaOptionalAction: (
    fileName: string,
    base64Image: string,
  ) => unknown;
  cedulaOptionalFileName: string;
  cedulaOptionalFileBase64: string;
  cedulaOptionalFileStatus: string;
  disable?: boolean;
  resetCedulaFilesAction: (fileId: string) => unknown;
  isCollectiveQuotation?: boolean;
};

export type PersonIdDataHook = {
  isFileUploaded: boolean;
  files: FileUpload[];
  disabled: boolean;
  failUpload: boolean;
  onLocalImageCaptured: (fileName: string, newImage: string) => void;
  onDeleteFile: (fileId: string) => void;
};

export function usePersonIdData({
  uploadCedulaAction,
  cedulaFileName,
  cedulaFileBase64,
  cedulaFileStatus,
  uploadCedulaOptionalAction,
  cedulaOptionalFileName,
  cedulaOptionalFileBase64,
  cedulaOptionalFileStatus,
  resetCedulaFilesAction,
  disable,
  isCollectiveQuotation,
}: PersonIdDataProps): PersonIdDataHook {
  const onLocalImageCaptured = useCallback(
    (fileName: string, newImage: string): void => {
      if (!cedulaFileBase64) {
        uploadCedulaAction(fileName, newImage);
      } else {
        uploadCedulaOptionalAction(fileName, newImage);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cedulaFileBase64],
  );

  const onDeleteFile = (fileId: string): void => {
    resetCedulaFilesAction(fileId);
  };

  const files: FileUpload[] = useMemo(() => {
    const fileList: FileUpload[] = [];
    if (cedulaFileBase64) {
      fileList.push({
        base64String: cedulaFileBase64,
        fileName: cedulaFileName,
        requestStatus: cedulaFileStatus,
        fileId: 'cedula',
      });
    }
    if (cedulaOptionalFileBase64) {
      fileList.push({
        base64String: cedulaOptionalFileBase64,
        fileName: cedulaOptionalFileName,
        requestStatus: cedulaOptionalFileStatus,
        fileId: 'cedula-optional',
      });
    }
    return fileList;
  }, [
    cedulaFileName,
    cedulaFileBase64,
    cedulaFileStatus,
    cedulaOptionalFileName,
    cedulaOptionalFileBase64,
    cedulaOptionalFileStatus,
  ]);

  const failUpload = useMemo(() => {
    return [cedulaFileStatus, cedulaOptionalFileStatus].includes('failure');
  }, [cedulaFileStatus, cedulaOptionalFileStatus]);

  const disabled = useMemo(() => {
    return (
      disable ||
      [cedulaFileStatus, cedulaOptionalFileStatus].includes('requesting')
    );
  }, [cedulaFileStatus, cedulaOptionalFileStatus, disable]);

  const isFileUploaded = useMemo(() => {
    return isCollectiveQuotation || files.length > 0;
  }, [isCollectiveQuotation, files.length]);

  return {
    isFileUploaded,
    files,
    disabled,
    failUpload,
    onLocalImageCaptured,
    onDeleteFile,
  };
}

export default usePersonIdData;
